import React, { useState, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';

interface Product {
  id: number;
  name: string;
}

interface ProductAutocompleteProps {
  products: Product[];
  onSelect: (product: Product) => void;
  placeholder?: string;
  disabled?: boolean;
}

const ProductAutocomplete: React.FC<ProductAutocompleteProps> = ({
  products,
  onSelect,
  placeholder = "Search product...",
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter products when search value changes
  useEffect(() => {
    if (!searchValue || searchValue.length < 2) {
      setFilteredProducts([]);
      setIsOpen(false);
      return;
    }

    const filtered = products.filter(product =>
      product.name.toLowerCase().includes(searchValue.toLowerCase())
    );

    setFilteredProducts(filtered);
    setIsOpen(filtered.length > 0);
  }, [searchValue, products]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleProductSelect = (product: Product) => {
    setSearchValue('');
    setIsOpen(false);
    onSelect(product);
  };

  const escapeRegExp = (input: string): string => {
    return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const highlightMatch = (text: string, search: string) => {
    if (!search) return text;

    const escaped = escapeRegExp(search);
    const regex = new RegExp(`(${escaped})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) => (
      index % 2 === 1 ? (
        <strong key={index} className="font-bold text-blue-600">{part}</strong>
      ) : (
        part
      )
    ));
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <Search className="h-4 w-4 text-gray-400" />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && filteredProducts.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {filteredProducts.map((product) => (
            <button
              key={product.id}
              type="button"
              onClick={() => handleProductSelect(product)}
              className="w-full px-4 py-3 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-b-0 transition-colors duration-150"
            >
              <div className="text-sm font-medium text-gray-900">
                {highlightMatch(product.name, searchValue)}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No results message */}
      {searchValue && searchValue.length >= 2 && filteredProducts.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-3">
          <div className="text-sm text-gray-500 text-center">
            No products found matching "{searchValue}"
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductAutocomplete;

